import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Edit, Trash2, History, CalendarIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { toast } from "@/hooks/use-toast";
import { OperationsHistoryModal } from "../common/OperationsHistoryModal";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useFacilityAvailability, FacilityAvailability } from "@/hooks/facility-availability/useFacilityAvailability";
import { useFacilityAvailabilityAuditLog } from "@/hooks/facility-availability/useFacilityAvailabilityAuditLog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API_CONFIG } from "@/config/envConfig";

export const FacilityAvailabilityTable = () => {
  const navigate = useNavigate();
  const { user, hasuraToken } = useAuth();
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState<FacilityAvailability | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<FacilityAvailability | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { auditLogs, loading: auditLoading, fetchAuditLogs, clearAuditLogs } = useFacilityAvailabilityAuditLog();
  
  const {
    facilityAvailabilities,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    fromDateFilter,
    toDateFilter,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    setFromDateFilter,
    setToDateFilter,
    handleSearch,
    clearSearch,
    refetch,
  } = useFacilityAvailability();

  const handleEdit = (item: FacilityAvailability) => {
    navigate(`/admin/facility-availability/edit/${item.facility_availability_id}`, { state: { facilityAvailability: item } });
  };

  const handleDeleteClick = (item: FacilityAvailability) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete || !user) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/appointment/${itemToDelete.facility_availability_id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${hasuraToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete facility availability');
      }

      toast({
        title: "Success",
        description: data.message || "Facility availability deleted successfully",
      });

      refetch();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete facility availability",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleHistory = (item: FacilityAvailability) => {
    setSelectedItemForHistory(item);
    setIsHistoryModalOpen(true);
    fetchAuditLogs(item.facility_availability_id);
  };

  const handleHistoryModalClose = (open: boolean) => {
    setIsHistoryModalOpen(open);
    if (!open) {
      clearAuditLogs();
      setSelectedItemForHistory(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const startIndex = (currentPage - 1) * entriesPerPage + 1;
  const endIndex = Math.min(currentPage * entriesPerPage, totalCount);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }
    
    return pages;
  };

  const showDateFilters = filterType === "availability_start_date" || filterType === "availability_end_date";

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Error loading facility availability: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="clinic_name">Clinic Name</SelectItem>
              <SelectItem value="facility_name">Facility Name</SelectItem>
              <SelectItem value="availability_start_date">Availability Start Date</SelectItem>
              <SelectItem value="availability_end_date">Availability End Date</SelectItem>
            </SelectContent>
          </Select>

          {!showDateFilters ? (
            <Input 
              className="w-64" 
              placeholder={`Search by ${filterType === 'clinic_name' ? 'Clinic Name' : 'Facility Name'}`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-40 justify-start text-left font-normal",
                      !fromDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {fromDateFilter ? format(fromDateFilter, "MM/dd/yyyy") : "From Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={fromDateFilter}
                    onSelect={setFromDateFilter}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-40 justify-start text-left font-normal",
                      !toDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {toDateFilter ? format(toDateFilter, "MM/dd/yyyy") : "To Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={toDateFilter}
                    onSelect={setToDateFilter}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          {(searchValue || fromDateFilter || toDateFilter) && (
            <Button 
              variant="outline"
              onClick={clearSearch}
              disabled={loading}
            >
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select 
            value={entriesPerPage.toString()} 
            onValueChange={(value) => setEntriesPerPage(Number(value))}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>
      
      <div id="table-admin-facility-availability-container" className="rounded-md border">
        <Table id="table-admin-facility-availability">
          <TableHeader>
            <TableRow id="table-admin-facility-availability-header-row">
              <TableHead id="header-cell-clinic-name">Clinic Name</TableHead>
              <TableHead id="header-cell-facility-name">Facility Name</TableHead>
              <TableHead id="header-cell-start-date">Availability Start Date</TableHead>
              <TableHead id="header-cell-end-date">Availability End Date</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-facility-availability-body">
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx} id={`row-facility-skeleton-${idx}`}>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                </TableRow>
              ))
            ) : facilityAvailabilities.length === 0 ? (
              <TableRow id="row-facility-availability-empty">
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No facility availability records found
                </TableCell>
              </TableRow>
            ) : (
              facilityAvailabilities.map((item) => (
                <TableRow key={item.facility_availability_id} id={`row-facility-availability-${item.facility_availability_id}`}>
                  <TableCell id={`cell-facility-${item.facility_availability_id}-clinic`} className="font-medium">{item.clinic_name}</TableCell>
                  <TableCell id={`cell-facility-${item.facility_availability_id}-name`}>{item.facility_name}</TableCell>
                  <TableCell id={`cell-facility-${item.facility_availability_id}-start`}>{formatDate(item.available_from_date)}</TableCell>
                  <TableCell id={`cell-facility-${item.facility_availability_id}-end`}>{formatDate(item.available_to_date)}</TableCell>
                  <TableCell id={`cell-facility-${item.facility_availability_id}-actions`} className="text-right">
                    <div className="flex justify-end gap-2">
                      <IconButtonWithTooltip
                        id={`btn-facility-${item.facility_availability_id}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleHistory(item)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-facility-${item.facility_availability_id}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-facility-${item.facility_availability_id}-delete`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(item)}
                        className="text-destructive hover:text-destructive"
                        disabled={isDeleting}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {startIndex} to {endIndex} of {totalCount} entries
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
              {getPageNumbers().map((pageNum) => (
                <PaginationItem key={pageNum}>
                  <PaginationLink 
                    onClick={() => setCurrentPage(pageNum)}
                    isActive={currentPage === pageNum}
                    className={currentPage === pageNum 
                      ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer" 
                      : "hover:bg-purple-100 cursor-pointer"
                    }
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={handleHistoryModalClose}
        entityName={selectedItemForHistory?.facility_name || ""}
        entityType="Facility Availability"
        auditLogs={auditLogs}
        loading={auditLoading}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Facility Availability</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this facility availability record for "{itemToDelete?.facility_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                // Prevent Radix AlertDialog from auto-closing so the loader stays visible
                e.preventDefault();
                void handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
